/**
 * Prediction Service
 * Reusable service for quick AI predictions across all sports dashboards
 */

import { v4 as uuidv4 } from 'uuid';
import { config } from '../../../../config';
import { getBrazilianStatus } from '../../../../utils/detectBrazil';
import { TEAM_ID_NAMESPACES, isBasketballV1Namespace } from '../../constants/predictionMeta';

const PREDICTION_SERVICE_IS_DEV = process.env.NODE_ENV !== 'production';

const makeTeamDescriptor = (teamLike, fallbackName) => {
  if (!teamLike) {
    return { name: fallbackName, id: null };
  }
  if (typeof teamLike === 'string') {
    return { name: teamLike || fallbackName, id: null };
  }
  return {
    name: teamLike.name || fallbackName,
    id: teamLike.id ?? null
  };
};

const resolveNBATeamsForPrediction = (game = {}) => {
  const teams = game.teams || {};
  const homeDescriptor = makeTeamDescriptor(teams.home || game.homeTeam || game.home, 'Home');
  const visitorsDescriptor = makeTeamDescriptor(
    teams.visitors || game.visitorsTeam || teams.away || game.awayTeam || game.away,
    'Visitors'
  );
  const awayDescriptor = makeTeamDescriptor(
    teams.away || game.awayTeam || game.away || visitorsDescriptor,
    'Away'
  );

  if (PREDICTION_SERVICE_IS_DEV) {
    console.log('[PredictionService] NBA team aliases resolved', {
      home: homeDescriptor.name,
      visitors: visitorsDescriptor.name,
      away: awayDescriptor.name,
      sourceTeams: Object.keys(teams || {})
    });
  }

  return {
    home: homeDescriptor,
    visitors: visitorsDescriptor,
    away: awayDescriptor
  };
};

const shouldIncludeNBATeamIds = (game) =>
  isBasketballV1Namespace(game?.predictionMeta?.teamIdNamespace);

const getNBATeamIdMetadata = (game, homeDescriptor, visitorsDescriptor, leagueId) => {
  if (!shouldIncludeNBATeamIds(game)) {
    if (PREDICTION_SERVICE_IS_DEV && (homeDescriptor?.id || visitorsDescriptor?.id)) {
      console.log('[PredictionService] Skipping NBA team IDs in prompt', {
        namespace: game?.predictionMeta?.teamIdNamespace || 'unknown'
      });
    }
    return { block: '', inline: '' };
  }

  const homeId = homeDescriptor?.id;
  const visitorsId = visitorsDescriptor?.id;

  if (!homeId || !visitorsId) {
    return { block: '', inline: '' };
  }

  const line = `Home Team ID: ${homeId}, Visitors Team ID: ${visitorsId}, League ID: ${leagueId ?? 'N/A'}`;
  return {
    block: `\n${line}`,
    inline: ` (${line})`
  };
};

class PredictionService {
  constructor() {
    this.apiUrl = config.apiUrl;
    this.isBrazilian = getBrazilianStatus();
  }

  /**
   * Send prediction request to backend API
   * @param {Object} params - Request parameters
   * @param {string} params.question - The prediction question/prompt
   * @param {string} params.userId - User ID
   * @param {Array} params.history - Conversation history (optional)
   * @param {string} params.language - Language preference (optional)
   * @returns {Promise<string>} - AI response
   */
  async sendPredictionRequest({ question, userId, history = [], language = 'en' }) {
    const sessionId = uuidv4();
    
    try {
      const requestPayload = {
        question: question,
        history: history.map(msg => ({
          role: msg.sender === 'user' ? 'user' : 'assistant',
          content: msg.text
        })),
        language: language || navigator.language || "en",
        isGuest: false,
        metadata: {
          userId: userId,
          sessionId: sessionId,
          conversationId: sessionId
        }
      };

      const response = await fetch(`${this.apiUrl}/ai/ask`, { 
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(requestPayload),
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.status}`);
      }

      // Handle streaming response
      let botResponse = "";
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        
        const lines = buffer.split('\n');
        buffer = lines.pop() || "";
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'content' && data.text) {
                botResponse += data.text;
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }

      // Process any remaining buffered content
      if (buffer && buffer.startsWith('data: ')) {
        try {
          const data = JSON.parse(buffer.slice(6));
          if (data.type === 'content' && data.text) {
            botResponse += data.text;
          }
        } catch (e) {
          console.error('Error parsing final SSE data:', e);
        }
      }

      return botResponse;
    } catch (error) {
      console.error("Error sending prediction request:", error);
      throw error;
    }
  }

  /**
   * Predict single game winner
   * @param {Object} game - Game/fixture data
   * @param {string} sport - Sport type (football, nba, mlb, etc.)
   * @param {string} userId - User ID
   * @returns {Promise<Object>} - Prediction result
   */
  async predictSingleGame({ game, sport, userId }) {
    const prompt = this.buildSingleGamePrompt(game, sport);
    
    try {
      const response = await this.sendPredictionRequest({
        question: prompt,
        userId: userId
      });

      return {
        success: true,
        prediction: response,
        game: game,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        game: game
      };
    }
  }

  /**
   * Predict multiple games at once
   * @param {Array} games - Array of games/fixtures
   * @param {string} sport - Sport type
   * @param {string} userId - User ID
   * @returns {Promise<Object>} - Batch prediction results
   */
  async predictMultipleGames({ games, sport, userId }) {
    const prompt = this.buildMultipleGamesPrompt(games, sport);
    
    try {
      const response = await this.sendPredictionRequest({
        question: prompt,
        userId: userId
      });

      return {
        success: true,
        predictions: response,
        games: games,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        games: games
      };
    }
  }

  /**
   * Build prompt for single game prediction
   * @private
   */
  buildSingleGamePrompt(game, sport) {
    if (sport === 'football' || sport === 'soccer') {
      const homeTeam = game.teams?.home?.name || game.homeTeam;
      const awayTeam = game.teams?.away?.name || game.awayTeam;
      const homeTeamId = game.teams?.home?.id || null;
      const awayTeamId = game.teams?.away?.id || null;
      const league = game.league?.name || game.competition;
      const leagueId = game.league?.id || null;
      
      // Build ID info string only if IDs exist
      let idInfo = '';
      if (homeTeamId && awayTeamId && leagueId) {
        idInfo = `\nHome Team ID: ${homeTeamId}, Away Team ID: ${awayTeamId}, League ID: ${leagueId}`;
      }
      
      if (this.isBrazilian) {
        return `PREVISÃO RÁPIDA - SEJA BREVE!

${homeTeam} vs ${awayTeam} (${league})${idInfo}

Preveja: [Vencedor] | [CONFIANÇA %] | [ALTA/MÉDIA/BAIXA] | [razão em 10-15 palavras]

IMPORTANTE: A confiança deve ser um número entre 0.1 e 99.9 (sem casas decimais além de uma)

Exemplo: Liverpool | 87.5 | ALTA | Forma superior e forte histórico em casa

RESPONDA EM PORTUGUÊS BRASILEIRO!`;
      }

      return `QUICK PICK - BE BRIEF!

${homeTeam} vs ${awayTeam} (${league})${idInfo}

Predict: [Winner] | [CONFIDENCE %] | [HIGH/MED/LOW] | [10-15 word reason]

IMPORTANT: Confidence must be a number between 0.1 and 99.9 (no more than one decimal place)

Example: Liverpool | 87.5 | HIGH | Superior form and strong home record vs United`;
    }
    
    if (sport === 'baseball' || sport === 'mlb') {
      const homeTeam = game.teams?.home?.name || game.homeTeam;
      const awayTeam = game.teams?.away?.name || game.awayTeam;
      const homeTeamId = game.teams?.home?.id;
      const awayTeamId = game.teams?.away?.id;
      const leagueId = game.league?.id || game.league;

      return `QUICK PICK - BE BRIEF!

${awayTeam} @ ${homeTeam}
Home Team ID: ${homeTeamId}, Away Team ID: ${awayTeamId}, League ID: ${leagueId}

Predict: [Winner] | [CONFIDENCE %] | [HIGH/MED/LOW] | [10-15 word reason]

IMPORTANT: Confidence must be a number between 0.1 and 99.9 (no more than one decimal place)

Example: Yankees | 72.5 | HIGH | Strong bullpen and recent offensive momentum`;
    }
    
    if (sport === 'mma' || sport === 'ufc') {
      const fighter1 = game.fighters?.first?.name || game.fighter1 || 'Fighter 1';
      const fighter2 = game.fighters?.second?.name || game.fighter2 || 'Fighter 2';
      const fighter1Id = game.fighters?.first?.id;
      const fighter2Id = game.fighters?.second?.id;
      const event = game.slug || game.event || 'MMA Event';
      const eventId = game.id || game.event_id;

      return `QUICK PICK - BE BRIEF!

${fighter1} vs ${fighter2} (${event})
Fighter 1 ID: ${fighter1Id}, Fighter 2 ID: ${fighter2Id}, Event ID: ${eventId}

Predict: [Winner] | [CONFIDENCE %] | [HIGH/MED/LOW] | [10-15 word reason]

IMPORTANT: Confidence must be a number between 0.1 and 99.9 (no more than one decimal place)

Example: McGregor | 85.0 | HIGH | Superior striking and reach advantage`;
    }
    
    if (sport === 'nfl') {
      const homeTeam = game.teams?.home?.name || game.homeTeam;
      const awayTeam = game.teams?.away?.name || game.awayTeam;
      const homeTeamId = game.teams?.home?.id;
      const awayTeamId = game.teams?.away?.id;
      const leagueId = game.league?.id || 1; // NFL league ID is typically 1
      
      return `QUICK PICK - NFL (PROFESSIONAL FOOTBALL) GAME - BE BRIEF!

**SPORT: NFL PROFESSIONAL AMERICAN FOOTBALL**
${awayTeam} @ ${homeTeam}
Home Team ID: ${homeTeamId}, Away Team ID: ${awayTeamId}, League ID: ${leagueId}

Predict the FOOTBALL winner: [Winner] | [CONFIDENCE %] | [HIGH/MED/LOW] | [10-15 word reason]

IMPORTANT: Confidence must be a number between 0.1 and 99.9 (no more than one decimal place)

Example: Chiefs | 78.5 | HIGH | Elite passing game and home field advantage

Note: This is NFL Professional Football. Focus on football factors like QB play, rushing, defense.`;
    }
    
    if (sport === 'ncaaf' || sport === 'college-football') {
      const homeTeam = game.teams?.home?.name || game.homeTeam;
      const awayTeam = game.teams?.away?.name || game.awayTeam;
      const homeTeamId = game.teams?.home?.id || null;
      const awayTeamId = game.teams?.away?.id || null;
      const conference = game.teams?.home?.conference || game.teams?.away?.conference || 'NCAA';
      const leagueId = game.league?.id || 2; // NCAAF league ID is typically 2

      // Build ID info string only if IDs exist
      let idInfo = '';
      if (homeTeamId && awayTeamId) {
        idInfo = `\nHome Team ID: ${homeTeamId}, Away Team ID: ${awayTeamId}, League ID: ${leagueId}`;
      }

      return `QUICK PICK - COLLEGE FOOTBALL (NCAAF) GAME - BE BRIEF!

**SPORT: AMERICAN COLLEGE FOOTBALL (NOT BASKETBALL)**
${awayTeam} @ ${homeTeam} (${conference} Conference)${idInfo}

Predict the FOOTBALL winner: [Winner] | [CONFIDENCE %] | [HIGH/MED/LOW] | [10-15 word reason]

IMPORTANT: Confidence must be a number between 0.1 and 99.9 (no more than one decimal place)

Example: Alabama | 82.3 | HIGH | Elite defense and dominant rushing attack

Note: This is American Football, NOT basketball. Focus on football factors like rushing, passing, defense.`;
    }

    if (sport === 'nba' || sport === 'basketball') {
      const { home, visitors } = resolveNBATeamsForPrediction(game);
      const homeTeam = home.name || 'Home';
      const visitorsTeam = visitors.name || 'Visitors';
      const leagueId = game.league?.id || 12; // NBA league ID is typically 12
      const idMetadata = getNBATeamIdMetadata(game, home, visitors, leagueId);

      if (this.isBrazilian) {
        return `PREVISÃO RÁPIDA - BASQUETE NBA - SEJA BREVE!

${visitorsTeam} @ ${homeTeam}${idMetadata.block}

Preveja: [Vencedor] | [CONFIANÇA %] | [ALTA/MÉDIA/BAIXA] | [razão em 15-20 palavras]

IMPORTANTE: A confiança deve ser um número entre 0.1 e 99.9 (sem casas decimais além de uma)

Exemplo: Lakers | 68.5 | MÉDIA | Defesa perimetral forte e vantagem de jogar em casa contra adversário cansado

RESPONDA EM PORTUGUÊS BRASILEIRO!`;
      }

      return `QUICK PICK - NBA BASKETBALL GAME - BE BRIEF!

${visitorsTeam} @ ${homeTeam}${idMetadata.block}

Predict: [Winner] | [CONFIDENCE %] | [HIGH/MED/LOW] | [15-20 word reason]

IMPORTANT: Confidence must be a number between 0.1 and 99.9 (no more than one decimal place)

Example: Lakers | 68.5 | MED | Strong perimeter defense and home court advantage against tired opponent

<Backend Tool Rule>
Use your get_basketball_odds EVERYTIME.
<Backend Tool Rule/>`

;
    }

    if (sport === 'nhl' || sport === 'hockey') {
      const homeTeam = game.teams?.home?.name || game.homeTeam;
      const awayTeam = game.teams?.away?.name || game.awayTeam;
      const homeTeamId = game.teams?.home?.id;
      const awayTeamId = game.teams?.away?.id;
      const leagueId = game.league?.id || 57; // NHL league ID is 57

      if (this.isBrazilian) {
        return `PREVISÃO RÁPIDA - HÓQUEI NHL - SEJA BREVE!

${awayTeam} @ ${homeTeam}
Home Team ID: ${homeTeamId}, Away Team ID: ${awayTeamId}, League ID: ${leagueId}

Preveja: [Vencedor] | [CONFIANÇA %] | [ALTA/MÉDIA/BAIXA] | [razão em 15-20 palavras]

IMPORTANTE: A confiança deve ser um número entre 0.1 e 99.9 (sem casas decimais além de uma)

Exemplo: Maple Leafs | 65.3 | MÉDIA | Goalie em forma excepcional e desempenho forte em casa contra rival divisional

RESPONDA EM PORTUGUÊS BRASILEIRO!`;
      }

      return `QUICK PICK - NHL HOCKEY GAME - BE BRIEF!

${awayTeam} @ ${homeTeam}
Home Team ID: ${homeTeamId}, Away Team ID: ${awayTeamId}, League ID: ${leagueId}

Predict: [Winner] | [CONFIDENCE %] | [HIGH/MED/LOW] | [15-20 word reason]

IMPORTANT: Confidence must be a number between 0.1 and 99.9 (no more than one decimal place)

Example: Maple Leafs | 65.3 | MED | Goalie in exceptional form and strong home performance against divisional rival`;
    }

    // Add other sports formats as needed
    return `Predict the winner of this ${sport} game: ${JSON.stringify(game)}`;
  }

  /**
   * Build prompt for multiple games prediction
   * @private
   */
  buildMultipleGamesPrompt(games, sport) {
    if (sport === 'football' || sport === 'soccer') {
      const gamesList = games.map((game, index) => {
        const homeTeam = game.teams?.home?.name || game.homeTeam;
        const awayTeam = game.teams?.away?.name || game.awayTeam;
        const homeTeamId = game.teams?.home?.id || null;
        const awayTeamId = game.teams?.away?.id || null;
        const leagueId = game.league?.id || null;
        
        // Only include IDs if they exist
        let idInfo = '';
        if (homeTeamId && awayTeamId && leagueId) {
          idInfo = ` (Home ID: ${homeTeamId}, Away ID: ${awayTeamId}, League ID: ${leagueId})`;
        }
        
        return `${index + 1}. ${homeTeam} vs ${awayTeam}${idInfo}`;
      }).join('\n');
      
      if (this.isBrazilian) {
        return `PREVISÃO RÁPIDA - ANALISE ESTES JOGOS EXATAMENTE COMO LISTADOS!

**JOGOS PARA PREVER (use EXATAMENTE estas partidas):**

${gamesList}

**REGRAS CRÍTICAS:**
1. Preveja APENAS os confrontos listados acima - não valide contra agenda oficial
2. Os IDs dos times são a fonte da verdade - use os times mostrados acima
3. NÃO questione os confrontos - apenas analise e preveja

**FORMATO OBRIGATÓRIO (uma linha por jogo):**
[Jogo #]: [Vencedor] | [% confiança] | [ALTA/MÉDIA/BAIXA] | [razão breve]

**IMPORTANTE:**
- Confiança: 0.1 a 99.9 (uma casa decimal)
- Use valores GENUÍNOS - evite 45.0, 50.0, 75.0
- Vencedor DEVE ser exatamente um dos times do jogo listado

**Exemplo:**
1: Flamengo | 72.8 | ALTA | Ataque forte e mandante
2: Ceará | 58.3 | MÉDIA | Melhor momento recente

PREVEJA OS ${games.length} JOGOS LISTADOS - SEM VALIDAÇÕES EXTERNAS!
RESPONDA EM PORTUGUÊS BRASILEIRO!`;
      }
      
      return `QUICK PICK - PREDICT THESE EXACT MATCHES AS LISTED!

**MATCHES TO PREDICT (use EXACTLY these matchups):**

${gamesList}

**CRITICAL RULES:**
1. Predict ONLY the matchups listed above - do not validate against official schedules
2. Team IDs are the source of truth - use the teams shown above
3. DO NOT question the matchups - just analyze and predict

**REQUIRED FORMAT (one line per game):**
[Game #]: [Winner] | [% confidence] | [HIGH/MED/LOW] | [brief reason]

**IMPORTANT:**
- Confidence: 0.1 to 99.9 (one decimal place)
- Use GENUINE values - avoid 45.0, 50.0, 75.0
- Winner MUST be exactly one of the teams in the listed game

**Example:**
1: Liverpool | 72.8 | HIGH | Strong attack and home advantage
2: Barcelona | 58.3 | MED | Better recent form

PREDICT THE ${games.length} LISTED GAMES - NO EXTERNAL VALIDATION!`;
    }
    
    if (sport === 'baseball' || sport === 'mlb') {
      const gamesList = games.map((game, index) => {
        const homeTeam = game.teams?.home?.name || game.homeTeam;
        const awayTeam = game.teams?.away?.name || game.awayTeam;
        const homeTeamId = game.teams?.home?.id;
        const awayTeamId = game.teams?.away?.id;
        const leagueId = game.league?.id || game.league;
        return `${index + 1}. ${awayTeam} @ ${homeTeam} (Home ID: ${homeTeamId}, Away ID: ${awayTeamId}, League ID: ${leagueId})`;
      }).join('\n');
      
      return `QUICK PICK DASHBOARD - BE CONCISE! No verbose explanations.

Predict these ${games.length} baseball games:

${gamesList}

For EACH game, respond with ONLY:
[Game #]: [Winner] | [CONFIDENCE %] | [HIGH/MED/LOW] | [5-10 word reason]

IMPORTANT: Confidence must be a number between 0.1 and 99.9 (no more than one decimal place)

Example format:
1: Yankees | 72.5 | HIGH | Better pitching matchup
2: Dodgers | 55.8 | MED | Strong offense despite injuries

BE BRIEF - MAX 15 WORDS PER GAME!`;
    }
    
    if (sport === 'mma' || sport === 'ufc') {
      const fightsList = games.map((fight, index) => {
        const fighter1 = fight.fighters?.first?.name || fight.fighter1 || 'Fighter 1';
        const fighter2 = fight.fighters?.second?.name || fight.fighter2 || 'Fighter 2';
        const fighter1Id = fight.fighters?.first?.id || null;
        const fighter2Id = fight.fighters?.second?.id || null;
        const eventId = fight.id || fight.event_id || null;
        
        // Only include IDs if they exist
        let idInfo = '';
        if (fighter1Id && fighter2Id && eventId) {
          idInfo = ` (Fighter 1 ID: ${fighter1Id}, Fighter 2 ID: ${fighter2Id}, Event ID: ${eventId})`;
        }
        
        return `${index + 1}. ${fighter1} vs ${fighter2}${idInfo}`;
      }).join('\n');
      
      return `QUICK PICK DASHBOARD - BE CONCISE! No verbose explanations.

Predict these ${games.length} MMA fights:

${fightsList}

For EACH fight, respond with ONLY:
[Fight #]: [Winner] | [CONFIDENCE %] | [HIGH/MED/LOW] | [5-10 word reason]

IMPORTANT: Confidence must be a number between 0.1 and 99.9 (no more than one decimal place)

Example format:
1: McGregor | 85.0 | HIGH | Superior striking and experience
2: Khabib | 62.5 | MED | Wrestling dominance despite layoff

BE BRIEF - MAX 15 WORDS PER FIGHT!`;
    }
    
    if (sport === 'nfl') {
      const gamesList = games.map((game, index) => {
        const homeTeam = game.teams?.home?.name || game.homeTeam;
        const awayTeam = game.teams?.away?.name || game.awayTeam;
        const homeTeamId = game.teams?.home?.id;
        const awayTeamId = game.teams?.away?.id;
        const leagueId = game.league?.id || 1; // NFL league ID is typically 1
        return `${index + 1}. ${awayTeam} @ ${homeTeam} (Home ID: ${homeTeamId}, Away ID: ${awayTeamId}, League ID: ${leagueId})`;
      }).join('\n');
      
      return `QUICK PICK - NFL PROFESSIONAL FOOTBALL - BE CONCISE!

**SPORT: NFL PROFESSIONAL AMERICAN FOOTBALL**
Predict these ${games.length} NFL FOOTBALL games:

${gamesList}

For EACH NFL game, respond with ONLY:
[Game #]: [Winner] | [CONFIDENCE %] | [HIGH/MED/LOW] | [5-10 word football reason]

IMPORTANT: Confidence must be a number between 0.1 and 99.9 (no more than one decimal place)

Example format:
1: Chiefs | 78.5 | HIGH | Superior QB play and offense
2: Bills | 58.2 | MED | Strong defense despite road game

Focus on NFL FOOTBALL factors: QB performance, offensive line, defense
BE BRIEF - MAX 15 WORDS PER GAME!`;
    }
    
    if (sport === 'ncaaf' || sport === 'college-football') {
      const gamesList = games.map((game, index) => {
        const homeTeam = game.teams?.home?.name || game.homeTeam;
        const awayTeam = game.teams?.away?.name || game.awayTeam;
        const homeTeamId = game.teams?.home?.id;
        const awayTeamId = game.teams?.away?.id;
        const leagueId = game.league?.id || 2; // NCAAF league ID is typically 2
        return `${index + 1}. ${awayTeam} @ ${homeTeam} (Home ID: ${homeTeamId}, Away ID: ${awayTeamId}, League ID: ${leagueId})`;
      }).join('\n');

      return `QUICK PICK - COLLEGE FOOTBALL (NCAAF) - BE CONCISE!

**SPORT: AMERICAN COLLEGE FOOTBALL (NOT BASKETBALL)**
Predict these ${games.length} FOOTBALL games:

${gamesList}

For EACH FOOTBALL game, respond with ONLY:
[Game #]: [Winner] | [CONFIDENCE %] | [HIGH/MED/LOW] | [5-10 word football reason]

IMPORTANT: Confidence must be a number between 0.1 and 99.9 (no more than one decimal place)

Example format:
1: Alabama | 82.3 | HIGH | Superior defense and rushing game
2: Georgia | 64.7 | MED | Strong offensive line despite injuries

Focus on FOOTBALL factors: rushing, passing, defense, special teams
BE BRIEF - MAX 15 WORDS PER GAME!`;
    }

    if (sport === 'nba' || sport === 'basketball') {
      const gamesList = games.map((game, index) => {
        const { home, visitors } = resolveNBATeamsForPrediction(game);
        const homeTeam = home.name || 'Home';
        const visitorsTeam = visitors.name || 'Visitors';
        const leagueId = game.league?.id || 12; // NBA league ID is typically 12
        const idMetadata = getNBATeamIdMetadata(game, home, visitors, leagueId);
        return `${index + 1}. ${visitorsTeam} @ ${homeTeam}${idMetadata.inline}`;
      }).join('\n');

      if (this.isBrazilian) {
        return `PAINEL DE PREVISÃO RÁPIDA - BASQUETE NBA - SEJA CONCISO! Sem explicações verbosas.

Preveja estes ${games.length} jogos de basquete da NBA:

${gamesList}

Para CADA jogo, responda APENAS:
[Jogo #]: [Vencedor] | [CONFIANÇA %] | [ALTA/MÉDIA/BAIXA] | [razão em 15-20 palavras]

IMPORTANTE: A confiança deve ser um número entre 0.1 e 99.9 (sem casas decimais além de uma)

Formato de exemplo:
1: Lakers | 68.5 | MÉDIA | Defesa forte e vantagem em casa contra adversário cansado
2: Warriors | 75.2 | ALTA | Arremesso superior e profundidade do elenco dominam jogo

Foco em fatores NBA: arremesso, defesa, ritmo, confrontos diretos
SEJA BREVE - MÁXIMO 20 PALAVRAS POR JOGO!
RESPONDA EM PORTUGUÊS BRASILEIRO!`;
      }

      return `QUICK PICK - NBA BASKETBALL - BE CONCISE!

Predict these ${games.length} NBA basketball games:

${gamesList}

For EACH game, respond with ONLY:
[Game #]: [Winner] | [CONFIDENCE %] | [HIGH/MED/LOW] | [15-20 word reason]

IMPORTANT: Confidence must be a number between 0.1 and 99.9 (no more than one decimal place)

Example format:
1: Lakers | 68.5 | MED | Strong defense and home court against tired opponent
2: Warriors | 75.2 | HIGH | Superior shooting and depth dominate the matchup

Focus on NBA factors: shooting, defense, pace, matchups
BE BRIEF - MAX 20 WORDS PER GAME!`;
    }

    if (sport === 'nhl' || sport === 'hockey') {
      const gamesList = games.map((game, index) => {
        const homeTeam = game.teams?.home?.name || game.homeTeam;
        const awayTeam = game.teams?.away?.name || game.awayTeam;
        const homeTeamId = game.teams?.home?.id;
        const awayTeamId = game.teams?.away?.id;
        const leagueId = game.league?.id || 57; // NHL league ID is 57
        return `${index + 1}. ${awayTeam} @ ${homeTeam} (Home ID: ${homeTeamId}, Away ID: ${awayTeamId}, League ID: ${leagueId})`;
      }).join('\n');

      if (this.isBrazilian) {
        return `PAINEL DE PREVISÃO RÁPIDA - HÓQUEI NHL - SEJA CONCISO! Sem explicações verbosas.

Preveja estes ${games.length} jogos de hóquei da NHL:

${gamesList}

Para CADA jogo, responda APENAS:
[Jogo #]: [Vencedor] | [CONFIANÇA %] | [ALTA/MÉDIA/BAIXA] | [razão em 15-20 palavras]

IMPORTANTE: A confiança deve ser um número entre 0.1 e 99.9 (sem casas decimais além de uma)

Formato de exemplo:
1: Maple Leafs | 67.8 | MÉDIA | Goalie em forma excepcional e forte desempenho em casa
2: Bruins | 72.5 | ALTA | Ataque poderoso e vantagem de mando dominam adversário

Foco em fatores NHL: goleiro, posse de puck, power play, defesa
SEJA BREVE - MÁXIMO 20 PALAVRAS POR JOGO!
RESPONDA EM PORTUGUÊS BRASILEIRO!`;
      }

      return `QUICK PICK - NHL HOCKEY - BE CONCISE!

Predict these ${games.length} NHL hockey games:

${gamesList}

For EACH game, respond with ONLY:
[Game #]: [Winner] | [CONFIDENCE %] | [HIGH/MED/LOW] | [15-20 word reason]

IMPORTANT: Confidence must be a number between 0.1 and 99.9 (no more than one decimal place)

Example format:
1: Maple Leafs | 67.8 | MED | Goalie in exceptional form and strong home performance
2: Bruins | 72.5 | HIGH | Powerful offense and home advantage dominate opponent

Focus on NHL factors: goaltending, puck possession, power play, defense
BE BRIEF - MAX 20 WORDS PER GAME!`;
    }

    return `Predict the winners of these ${sport} games: ${JSON.stringify(games)}`;
  }
}

// Export singleton instance
export default new PredictionService();
